import {TransactionDirection, TransactionGroupBy} from '@sollapay/enums';
import {Badge} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {dateToIso, formatNIS, parseMoney} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {formatAssetName} from '@/features/buyers/utils';

import type {TrustAccountTransactionDTO, TrustAccountTransactionGroupDTO} from '@sollapay/types';
import type {TFunction} from 'i18next';
import type {FC} from 'react';

interface TransactionGroupHeaderProps {
  group: TrustAccountTransactionGroupDTO;
  groupBy: TransactionGroupBy;
}

function resolveDateLabel(dateKey: string, t: TFunction, language: string): string {
  const todayStr = dateToIso(new Date());
  const yesterdayStr = dateToIso(new Date(Date.now() - 86_400_000));
  const dayBeforeStr = dateToIso(new Date(Date.now() - 2 * 86_400_000));

  if (dateKey === todayStr) return t('view.transactions.date.today');
  if (dateKey === yesterdayStr) return t('view.transactions.date.yesterday');
  if (dateKey === dayBeforeStr) return t('view.transactions.date.dayBefore');

  return new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(new Date(dateKey));
}

function resolveGroupLabel(
  group: TrustAccountTransactionGroupDTO,
  groupBy: TransactionGroupBy,
  t: TFunction,
  language: string
): string {
  const {groupKey, transactions} = group;
  const firstTx: TrustAccountTransactionDTO | undefined = transactions[0];

  switch (groupBy) {
    case TransactionGroupBy.DATE:
      return resolveDateLabel(groupKey, t, language);
    case TransactionGroupBy.COUNTERPARTY:
      if (firstTx?.direction === TransactionDirection.CREDIT) {
        return firstTx.buyerName ?? groupKey;
      }
      if (firstTx?.direction === TransactionDirection.DEBIT) {
        return firstTx.creditorName ?? groupKey;
      }
      return groupKey;
    case TransactionGroupBy.PURCHASE:
      if (firstTx?.direction === TransactionDirection.CREDIT && firstTx.asset) {
        return formatAssetName(firstTx.asset, t);
      }
      return groupKey;
    case TransactionGroupBy.STATUS:
      return t(`view.transactions.status.${groupKey}`);
    case TransactionGroupBy.INITIATION_METHOD:
      return groupKey === 'unknown'
        ? t('view.transactions.initiationMethod.unknown')
        : t(`view.transactions.initiationMethod.${groupKey}`);
    case TransactionGroupBy.DIRECTION:
      return t(`view.transactions.direction.${groupKey}`);
  }
}

export const TransactionGroupHeader: FC<TransactionGroupHeaderProps> = ({group, groupBy}) => {
  const {t, i18n} = useTranslation('trustAccounts');
  const groupLabel = resolveGroupLabel(group, groupBy, t, i18n.language);
  const balance = parseMoney(group.totalBalanceNis);
  const isPositive = balance.gte(0);
  const formattedBalance = formatNIS(balance);

  return (
    <div
      data-slot="transaction-group-header"
      className="flex items-center justify-between border-b border-border bg-background-secondary px-4 py-3"
    >
      <div className="flex items-center gap-2">
        <span data-slot="label" className="text-sm font-semibold text-fg">
          {groupLabel}
        </span>
        <Badge variant="neutral" size="sm">
          {t('view.transactions.group.transactionCount', {count: group.transactionCount})}
        </Badge>
      </div>

      <span
        data-slot="total-balance"
        dir="ltr"
        className={cn('text-sm font-semibold', isPositive ? 'text-fg-credit' : 'text-destructive')}
      >
        {formattedBalance}
      </span>
    </div>
  );
};
